import type { FromModel, ModelDefinition } from "@/__main__/data-model.mjs";
import createModel, { Silent, Tuple } from "@/__main__/data-model.mjs";
// import { RANK_SYMBOLS } from "@/game-lol/constants/constants-ranks.mjs";
// import { ROLE_SYMBOLS } from "@/game-lol/utils/roles.mjs";
import QueueSymbol from "@/game-lol/utils/symbol-queue.mjs";
import { RankSymbol } from "@/game-lol/utils/symbol-rank.mjs";
import RoleSymbol from "@/game-lol/utils/symbol-role.mjs";

const COLUMN_COUNT = 19;
const COLUMNS = Array.from({ length: COLUMN_COUNT }, () => String);

const buildModel = Tuple(COLUMNS) satisfies ModelDefinition;

const columnModel = {
  name: String,
  position: Number,
  typeName: String,
  typeText: String,
};

const SynergyResult = {
  patch: String,
  queue_id: QueueSymbol,
  champion_id: Number,
  championId: Number,
  individual_position: RoleSymbol,
  ally_champion_id: Number,
  allyChampionId: Number,
  ally_individual_position: RoleSymbol,
  tier: RankSymbol,
  region: String,
  filters: String,
  games: Number,
  wins: Number,
  win_rate: Number,
  champion_num_games: Number,
  champion_win_rate: Number,
  ally_champion_num_games: Number,
  ally_champion_win_rate: Number,
  win_rate_delta: Number,
  champion_synergy_tier: Number,
  synergy_tier: Number,
} satisfies ModelDefinition;

const SynergiesModel = createModel({
  data: {
    executeDatabricksQuery: {
      payload: {
        manifest: {
          schema: {
            columnCount: Number,
            columns: [columnModel],
          },
        },
        result: {
          chunkIndex: Silent(Number),
          dataArray: [buildModel],
          rowCount: Silent(Number),
          rowOffset: Silent(Number),
        },
      },
    },
  },
}).transform([SynergyResult], (data) => {
  const {
    data: { executeDatabricksQuery },
  } = data;

  const { columns = [] } =
    executeDatabricksQuery?.payload?.manifest?.schema || {};

  const list = executeDatabricksQuery?.payload?.result?.dataArray || [];

  const champions = list.map((c) => {
    const result = {
      patch: "",
      queue_id: null,
      champion_id: 0,
      championId: 0,
      individual_position: null,
      tier: null,
      region: "",
      ally_champion_id: 0,
      allyChampionId: 0,
      ally_individual_position: null,
      games: 0,
      wins: 0,
      win_rate: 0,
      champion_num_games: 0,
      champion_win_rate: 0,
      ally_champion_num_games: 0,
      ally_champion_win_rate: 0,
      win_rate_delta: 0,
      champion_synergy_tier: 0,
      synergy_tier: 0,
      filters: "",
    };
    for (const column of columns) {
      const value: string = (c[column.position] || "") as unknown as string;
      result[column.name] = castType(column.typeText, value);
    }

    result.championId = result.champion_id;
    result.allyChampionId = result.ally_champion_id;

    return result;
  });

  return champions.sort(
    (a, b) => a.synergy_tier - b.synergy_tier || b.win_rate - a.win_rate,
  );
});

export default SynergiesModel;

export type ChampionSynergies = FromModel<typeof SynergiesModel>;

function castType(typeText: string, value: string) {
  switch (typeText) {
    case "BOOLEAN":
      return value === "true";
    case "DOUBLE":
    case "BIGINT":
    case "LONG":
    case "INT":
      return value ? Number(value) : 0;
    case "STRING":
    case undefined:
    default:
      return value ?? "";
  }
}
